import { useMemo } from "react";
import { Area, ComposedChart, CartesianGrid, Line, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Activity } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { PessoaAcessoResumoDTO } from "@/types/pessoa-acesso.type";

const CHART_CONFIG = {
  pessoas: { label: "Pessoas que acessaram", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

function formatarSerie(acessosPorDia: PessoaAcessoResumoDTO["acessosPorDia"]) {
  return acessosPorDia.map((r) => ({
    ...r,
    // `r.dia` é "YYYY-MM-DD" (coluna `date`) — parseado direto, o `new Date` cai em UTC e retrocede um dia em fuso negativo.
    label: format(new Date(`${r.dia}T00:00:00`), "dd/MM", { locale: ptBR }),
  }));
}

interface PulseDotProps {
  cx?: number;
  cy?: number;
  index?: number;
  dataLength: number;
}

/** Ponto normal invisível; só o último dia (hoje) ganha destaque, como um pulso vivo. */
function PulseDot({ cx, cy, index, dataLength }: PulseDotProps) {
  if (index !== dataLength - 1) return <g />;
  return (
    <g>
      <circle cx={cx} cy={cy} r={7} fill="hsl(var(--primary))" opacity={0.18} />
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="hsl(var(--primary))"
        stroke="hsl(var(--card))"
        strokeWidth={2}
      />
    </g>
  );
}

export function AcessosPorDiaChart({
  resumo,
  loading,
}: {
  resumo: PessoaAcessoResumoDTO;
  loading: boolean;
}) {
  const serie = useMemo(() => formatarSerie(resumo.acessosPorDia), [resumo]);
  const pico = serie.length > 0 ? Math.max(...serie.map((s) => s.pessoas)) : 0;
  const media =
    serie.length > 0
      ? (serie.reduce((soma, s) => soma + s.pessoas, 0) / serie.length).toFixed(1)
      : "0";

  return (
    <div className="h-full rounded-xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">Evolução dos acessos</p>
        </div>
      </div>

      <p className="mb-5 text-xs text-muted-foreground">
        Pessoas distintas a cada dia. Uma pessoa conta uma vez por dia.
      </p>
      {loading ? (
        <Skeleton className="h-64 w-full rounded-lg" />
      ) : pico === 0 ? (
        <p className="flex h-64 items-center justify-center text-center text-sm italic text-muted-foreground">
          Nenhum acesso registrado no período. Experimente selecionar outro período.
        </p>
      ) : (
        <>
          <ChartContainer config={CHART_CONFIG} className="!aspect-auto h-64 w-full">
            <ComposedChart
              accessibilityLayer
              data={serie}
              margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
            >
              <defs>
                <linearGradient id="fillAcessos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                className="text-[11px]"
                interval="preserveStartEnd"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={24}
                allowDecimals={false}
                className="text-[11px]"
              />
              <ChartTooltip
                cursor={{ stroke: "hsl(var(--muted-foreground))", strokeDasharray: "3 3" }}
                content={(props) => (
                  <ChartTooltipContent
                    active={props.active}
                    label={props.label}
                    // Area + Line compartilham dataKey="pessoas" (uma só desenha o preenchimento, a outra a linha);
                    // sem isso o payload do tooltip vem com a mesma série duplicada.
                    payload={props.payload?.filter(
                      (item, index, arr) =>
                        arr.findIndex((other) => other.dataKey === item.dataKey) === index,
                    )}
                    formatter={(value) => (
                      <span className="font-mono font-semibold text-foreground">
                        {value} {Number(value) === 1 ? "pessoa" : "pessoas"}
                      </span>
                    )}
                  />
                )}
              />
              <Area
                dataKey="pessoas"
                fill="url(#fillAcessos)"
                stroke="none"
                isAnimationActive={false}
              />
              <Line
                dataKey="pessoas"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={(props: PulseDotProps) => (
                  <PulseDot key={props.index} {...props} dataLength={serie.length} />
                )}
                activeDot={{ r: 4, fill: "hsl(var(--primary))" }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>

          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {serie[0]?.label} – {serie[serie.length - 1]?.label}
            </span>
            <span>
              pico <span className="font-mono font-medium text-foreground">{pico}</span> · média{" "}
              <span className="font-mono font-medium text-foreground">{media}</span>/dia
            </span>
          </div>
        </>
      )}
    </div>
  );
}
